import { z } from 'zod';
import { EnvironmentSchema } from './environment';

/** Backing services the API depends on. Grows as later briefs add services. */
const DependencyNameSchema = z.enum(['database']);
export type DependencyName = z.infer<typeof DependencyNameSchema>;

/** One dependency's health; `latencyMs` is null when its check failed. */
const DependencyStatusSchema = z.object({
  name: DependencyNameSchema,
  status: z.enum(['ok', 'down']),
  latencyMs: z.number().nonnegative().nullable(),
});
export type DependencyStatus = z.infer<typeof DependencyStatusSchema>;

/** Response of `GET /health/live`: the process is up. No dependency checks. */
export const HealthLiveSchema = z.object({
  status: z.literal('ok'),
});

/**
 * Response of `GET /health/ready`: 200 when every dependency is up, 503 otherwise. Load
 * balancers and deploys read only the status code (D-067).
 */
export const HealthReadySchema = z.object({
  status: z.enum(['ok', 'unavailable']),
  dependencies: z.array(DependencyStatusSchema),
});
export type HealthReady = z.infer<typeof HealthReadySchema>;

/**
 * Response of `GET /v1/system/status`. Always returned while the API runs;
 * `degraded` means at least one dependency is down (D-041, D-043).
 */
export const SystemStatusSchema = z.object({
  status: z.enum(['ok', 'degraded']),
  service: z.string().min(1),
  version: z.string().min(1),
  commit: z.string().min(1),
  environment: EnvironmentSchema,
  serverTime: z.iso.datetime(),
  requestId: z.string().min(1),
  dependencies: z.array(DependencyStatusSchema),
});
export type SystemStatus = z.infer<typeof SystemStatusSchema>;

/**
 * Response of `GET /v1/system/whoami`: the signed-in person, as the API verified them
 * from their Firebase ID token (D-059). Temporary, to check sign-in end to end; it
 * goes once a real screen needs the user.
 */
export const WhoAmISchema = z.object({
  uid: z.string().min(1),
  email: z.string().nullable(),
  emailVerified: z.boolean(),
  displayName: z.string().nullable(),
  /** How they signed in, such as `password`, or `custom` after joining a shared sign-in (D-060). */
  signInProvider: z.string().min(1),
  signedInAt: z.iso.datetime(),
  tokenExpiresAt: z.iso.datetime(),
});
export type WhoAmI = z.infer<typeof WhoAmISchema>;
